/**
 * ReprintDrawer — slide-out panel for creating V2 reprint orders.
 */
import { Button, tokens } from "@blurb/codex-react";
import { createStyles } from "@mantine/core";
import { GraphQL, isGraphQLError } from "@rpi/openapi-api";
import { RpiAlert, useToasts } from "@rpi/openapi-core";
import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { RpaDrawer } from "../../rpi-core/drawer/RpaDrawer";
import { Body, Label } from "../../components/Typography";
import { REPRINT_REASONS } from "./reprintReasons";
import { RpaDropdown } from "../../components/inputFields/RpaDropdownInput";
import { RpaRadio, RpaRadioGroup } from "../../components/inputFields/RpaRadioInput";
import { OrderItemSelector, ValidItem } from "./OrderItemSelector";

const SHIPPING_LABELS: Record<GraphQL.OpenapiProvidedShippingClassification, string> = {
  [GraphQL.OpenapiProvidedShippingClassification.Economy]: "Economy",
  [GraphQL.OpenapiProvidedShippingClassification.Standard]: "Standard",
  [GraphQL.OpenapiProvidedShippingClassification.Express]: "Express",
  [GraphQL.OpenapiProvidedShippingClassification.Priority]: "Priority",
};

const SHIPPING_OPTIONS = Object.entries(SHIPPING_LABELS).map(([value, label]) => ({ value, label }));

const useStyles = createStyles({
  body: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[4],
    padding: `0 ${tokens.spacing[6]}`,
  },
  footer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: tokens.spacing[3],
    padding: tokens.spacing[6],
  },
  cancelLink: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
    alignSelf: "center",
  },
  cancelText: {
    color: tokens.color.semantic.text.link,
    borderBottom: `1px solid currentColor`,
    paddingBottom: 2,
  },
});

/* ---------- Types & helpers ---------- */

interface ReprintDrawerProps {
  opened: boolean;
  onClose: () => void;
  order: GraphQL.OrderQuery["orderByIdentifier"];
}

/**
 * Attempts to match an order's shippingMethodCode to a known shipping
 * classification. Returns undefined when there is no match so the
 * select falls back to Economy.
 */
function resolveShippingClassification(
  shippingMethodCode: string | undefined | null
): GraphQL.OpenapiProvidedShippingClassification | undefined {
  if (!shippingMethodCode) return undefined;
  const upper = shippingMethodCode.toUpperCase();
  const values: string[] = Object.values(GraphQL.OpenapiProvidedShippingClassification);
  if (values.includes(upper)) return upper as GraphQL.OpenapiProvidedShippingClassification;
  return undefined;
}

/* Drawer Component */
export function ReprintDrawer({ opened, onClose, order }: ReprintDrawerProps) {
  const { showSuccessToast, showErrorToast } = useToasts();
  const queryClient = useQueryClient();

  const { mutate: submitReprint, isLoading } = GraphQL.useReprintOrderV2Mutation({
    onSuccess: async (data) => {
      const newOrderId = data.orderReprintCreateV2ByIdentifier.customerOrderId;
      showSuccessToast(`The Reprint Order was created successfully! (Order ${newOrderId})`);
      await queryClient.invalidateQueries({ queryKey: ["Order"] });
      onClose();
    },
    onError: (error) => {
      if (isGraphQLError(error)) {
        showErrorToast(error.message);
      } else {
        showErrorToast("Failed to create reprint order. Please try again.");
      }
    },
  });

  /** Prevent closing mid-submission to avoid orphaned requests. */
  const handleClose = () => {
    if (!isLoading) onClose();
  };

  return (
    <RpaDrawer title={`Reprint ${order?.customerOrderId ?? ""}`} opened={opened} onClose={handleClose}>
      <ReprintDrawerContents order={order} isLoading={isLoading} onClose={handleClose} submit={submitReprint} />
    </RpaDrawer>
  );
}

interface ReprintDrawerContentProps {
  order: GraphQL.OrderQuery["orderByIdentifier"];
  isLoading: boolean;
  submit: (input: GraphQL.ReprintOrderV2MutationVariables) => void;
  onClose: () => void;
}

/**
 * Drawer contents
 * Implemented as a separate child component so state resets when the drawer closes
 */
function ReprintDrawerContents({ order, isLoading, submit, onClose }: ReprintDrawerContentProps) {
  const { classes } = useStyles();

  const [reason, setReason] = React.useState<string | undefined>(undefined);
  const [shippingClassification, setShippingClassification] =
    React.useState<GraphQL.OpenapiProvidedShippingClassification>(
      resolveShippingClassification(order?.destination?.shippingMethodCode) ??
        GraphQL.OpenapiProvidedShippingClassification.Economy
    );
  const [isFreeOfCharge, setIsFreeOfCharge] = React.useState<boolean | undefined>(undefined);
  const [submitted, setSubmitted] = React.useState(false);

  const [validItems, setValidItems] = React.useState<ValidItem[]>([]);

  const orderItems = order?.orderItems ?? [];

  const missingReason = !reason;
  const missingItems = validItems.length === 0;
  const missingPayment = isFreeOfCharge === undefined;

  const handleSubmit = () => {
    setSubmitted(true);
    if (!reason || !shippingClassification || isFreeOfCharge === undefined || validItems.length === 0) return;
    if (!order?.customer?.publicId || !order?.customerOrderId) return;

    submit({
      input: {
        reason,
        shippingClassification,
        isFreeOfCharge,
        orderIdentifier: {
          customerPublicId: order.customer.publicId,
          customerOrderId: order.customerOrderId,
        },
        reprintedItems: validItems.map((item) => ({
          orderItemId: item.orderItemId,
          quantity: item.quantity,
        })),
      },
    });
  };

  return (
    <>
      <div className={classes.body}>
        <RpaDropdown
          label={
            <Label size="base" weight="semibold">
              Reason
            </Label>
          }
          placeholder="Select.."
          searchable
          creatable
          getCreateLabel={(query) => query}
          onCreate={(query) => {
            setReason(query);
            return query;
          }}
          data={REPRINT_REASONS}
          value={reason ?? null}
          onChange={(val) => setReason(val ?? undefined)}
          error={submitted && missingReason ? "Please select a reason" : undefined}
        />

        <RpaDropdown
          label={
            <Label size="base" weight="semibold">
              Shipping Method
            </Label>
          }
          placeholder="Select.."
          data={SHIPPING_OPTIONS}
          value={shippingClassification}
          onChange={(val) => {
            const match = Object.values(GraphQL.OpenapiProvidedShippingClassification).find((v) => v === val);
            if (match) setShippingClassification(match);
          }}
        />

        <OrderItemSelector
          orderItems={orderItems}
          onValidItemsChange={setValidItems}
          error={submitted && missingItems ? "Please add an item" : undefined}
        />

        <div>
          <RpaRadioGroup
            label={
              <Label size="base" weight="semibold">
                Payment Method
              </Label>
            }
            value={isFreeOfCharge === undefined ? "" : isFreeOfCharge ? "free" : "charge"}
            onChange={(val) => setIsFreeOfCharge(val === "free")}
          >
            <RpaRadio value="charge" label="Charge as Normal" />
            <RpaRadio value="free" label="No Payment - No Cost Reprint" />
          </RpaRadioGroup>
          {submitted && missingPayment && <RpiAlert>Please select the payment method</RpiAlert>}
        </div>
      </div>
      <RpaDrawer.Footer>
        <div className={classes.footer}>
          <Button variant="filled" intent="primary" size="large" loading={isLoading} onClick={handleSubmit}>
            Submit Reprint
          </Button>
          <button className={classes.cancelLink} onClick={onClose} disabled={isLoading}>
            <Body size="base" weight="semibold" className={classes.cancelText}>
              Cancel
            </Body>
          </button>
        </div>
      </RpaDrawer.Footer>
    </>
  );
}
